import { useEffect, useState, useCallback } from 'react';
import Navbar from '@/components/Navbar';
import Hero from '@/components/Hero';
import About from '@/components/About';
import Skills from '@/components/Skills';
import Projects from '@/components/Projects';
import Services from '@/components/Services';
import Testimonials from '@/components/Testimonials';
import Contact from '@/components/Contact';
import Footer from '@/components/Footer';
import MobileNav from '@/components/MobileNav';
import ThemeProvider from '@/components/ThemeProvider';
import LoadingScreen from '@/components/LoadingScreen';
import SEOHead from '@/components/SEOHead';
import { seedDefaultData, readData } from '@/firebase/database';

const Index = () => {
  const [loadingDone, setLoadingDone] = useState(false);
  const [loadingEnabled, setLoadingEnabled] = useState<boolean | null>(null);
  const [loadingDuration, setLoadingDuration] = useState(3000);
  const [socialSameAs, setSocialSameAs] = useState<string[]>([]);

  useEffect(() => {
    // Pull social links from Firebase to strengthen Person entity sameAs signal
    readData('nfc/socials').then((s) => {
      if (s && typeof s === 'object') {
        const urls = (Object.values(s) as Array<{ url?: string }>)
          .map((x) => (typeof x?.url === 'string' ? x.url.trim() : ''))
          .filter((u) => {
            if (!u || !/^https?:\/\//i.test(u)) return false;
            try { new URL(u); return true; } catch { return false; }
          });
        setSocialSameAs(Array.from(new Set(urls)));
      }
    }).catch(() => {});
  }, []);


  useEffect(() => {
    seedDefaultData().catch(() => {});

    // Timeout fallback: if Firebase doesn't respond in 4s, show page anyway
    const timeout = setTimeout(() => {
      setLoadingEnabled((prev) => (prev === null ? false : prev));
    }, 4000);

    readData('settings')
      .then((s) => {
        clearTimeout(timeout);
        if (s) {
          setLoadingEnabled(s.loadingScreenEnabled ?? false);
          setLoadingDuration(s.loadingScreenDuration ? Number(s.loadingScreenDuration) * 1000 : 3000);
        } else {
          setLoadingEnabled(false);
        }
      })
      .catch(() => {
        clearTimeout(timeout);
        setLoadingEnabled(false);
      });

    return () => clearTimeout(timeout);
  }, []);

  const handleLoadComplete = useCallback(() => setLoadingDone(true), []);

  // Show a spinner while waiting for settings, instead of returning null
  if (loadingEnabled === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-transparent border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Mustakim Nur Salin — Web Developer & UI/UX Designer"
        description="I’m Mustakim Nur Salin, also known as RealCodeMind — a passionate teenage frontend developer from Dhaka, Bangladesh. I specialize in HTML, CSS, and JavaScript, focusing purely on frontend development. As a Creative Director and UI/UX Designer, I turn complex ideas into simple, beautiful, and user-friendly designs. I build websites that are functional, visually attractive, intuitive, and impactful. When I'm not coding, I'm contributing to open-source, writing technical articles, or exploring the latest web technologies."
        path="/"
        keywords="Mustakim Nur Salin, Mustakim Nur, Mustakim Error, realcodemind, web developer, UI/UX designer, frontend developer, Bangladesh, portfolio"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": "https://mustakim.iam.bd/#webpage",
            "url": "https://mustakim.iam.bd/",
            "name": "Mustakim Nur Salin — Web Developer & UI/UX Designer",
            "isPartOf": { "@id": "https://mustakim.iam.bd/#website" },
            "about": { "@id": "https://mustakim.iam.bd/#person" },
            "primaryImageOfPage": {
              "@type": "ImageObject",
              "url": "https://blogger.googleusercontent.com/img/a/AVvXsEhWK3cFUmvQkwYWf4Nsmc46ZDYxUepfkxKIV1u-RY3pO3alDyR-TE8iVBpk-yDEDfoRHr0WI9K8RxzVtWQVgqQwbUHsP4XKsgeAXxiOZ00qUbqUzF9JEET-O39ycTxDkHayGzSqIlfbuRCyWE_blySPVY4oA7e9BJ4LxpdUHZeoedCvnMDAE7njscle_oE=s1600",
              "contentUrl": "https://blogger.googleusercontent.com/img/a/AVvXsEhWK3cFUmvQkwYWf4Nsmc46ZDYxUepfkxKIV1u-RY3pO3alDyR-TE8iVBpk-yDEDfoRHr0WI9K8RxzVtWQVgqQwbUHsP4XKsgeAXxiOZ00qUbqUzF9JEET-O39ycTxDkHayGzSqIlfbuRCyWE_blySPVY4oA7e9BJ4LxpdUHZeoedCvnMDAE7njscle_oE=s1600",
              "name": "Mustakim Nur Salin",
              "creator": { "@type": "Person", "name": "Mustakim Nur Salin", "url": "https://mustakim.iam.bd/" },
              "creditText": "Mustakim Nur Salin",
              "copyrightNotice": `© ${new Date().getFullYear()} Mustakim Nur Salin. All rights reserved.`,
              "license": "https://mustakim.iam.bd/",
              "acquireLicensePage": "https://mustakim.iam.bd/contact"
            },
            "inLanguage": "en"
          },
          {
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            "@id": "https://mustakim.iam.bd/#profilepage",
            "url": "https://mustakim.iam.bd/",
            "name": "Mustakim Nur Salin — Official Profile",
            "isPartOf": { "@id": "https://mustakim.iam.bd/#website" },
            "mainEntity": {
              "@type": "Person",
              "@id": "https://mustakim.iam.bd/#person",
              "name": "Mustakim Nur Salin",
              "alternateName": ["RealCodeMind", "Md Mustakim Nur Salin", "Mustakim Error", "realcodemind"],
              "url": "https://mustakim.iam.bd/",
              "sameAs": Array.from(new Set([
                "https://mustakim.iam.bd/",
                "https://www.facebook.com/mustakim.error",
                "https://www.instagram.com/mustakim.error",
                "https://t.me/mustakim_error",
                "https://twitter.com/mustakim_error",
                "https://github.com/mustakim-error",
                "https://www.linkedin.com/in/mustakim-error",
                ...socialSameAs,
              ]))
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://mustakim.iam.bd/" }
            ]
          }
        ]}
      />

      <ThemeProvider />
      {loadingEnabled && !loadingDone && (
        <LoadingScreen duration={loadingDuration} enabled={loadingEnabled} onComplete={handleLoadComplete} />
      )}
      {(!loadingEnabled || loadingDone) && (
        <>
          <Navbar />
          <main>
            <Hero />
            <About />
            <Skills />
            <Projects />
            <Services />
            <Testimonials />
            <Contact />
          </main>
          <Footer />
          <MobileNav />
        </>
      )}
    </div>
  );
};

export default Index;
